import { memo, useCallback } from 'react';

import { type LibraryWithPermissions } from '@air/types';

import { type DragLibraryItem, DragType, type RearrangableItem } from '~/components/Shared/Drag/dragTypes';
import { DNDRearrangeArea } from '~/components/v2/components/shared/dnd/DNDRearrangeArea';
import { useScrollElementContext } from '~/components/v2/components/shared/scroll/ScrollElementContextProvider';
import { useIsScrollElementVisible } from '~/components/v2/components/shared/scroll/useIsScrollElementVisible';
import { WorkspaceSidebarLibrariesListItem } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarLibrariesListItem';
import { WorkspaceSidebarLibrariesListItemGeneral } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarLibrariesListItemGeneral';
import { WorkspaceSidebarVirtualizedList } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarVirtualizedList';

const DRAG_TYPES = [DragType.library];

export type WorkspaceSidebarLibrariesListProps = {
  libraries: LibraryWithPermissions[];
  draggable?: boolean;
  onLibraryRearrange?: (props: {
    adjacentItem: RearrangableItem;
    libraries: LibraryWithPermissions[];
    library: DragLibraryItem;
  }) => void;
};

export const WorkspaceSidebarLibrariesList = memo(
  ({ libraries, draggable = false, onLibraryRearrange }: WorkspaceSidebarLibrariesListProps) => {
    const { scrollElement } = useScrollElementContext();
    const isScrollElementVisible = useIsScrollElementVisible(scrollElement);

    const canRearrangeLibrary = useCallback(
      (index: number) => (draggedLibrary: DragLibraryItem) => {
        if (!draggable) {
          return false;
        }

        // Don't allow dropping in the same position
        if (draggedLibrary.index === index || draggedLibrary.index === index + 1) {
          return false;
        }

        return true;
      },
      [draggable],
    );

    const handleItemRearrange = useCallback(
      (index: number) => (draggedLibrary: DragLibraryItem) => {
        if (onLibraryRearrange) {
          onLibraryRearrange({
            adjacentItem: {
              direction: 'after',
              index,
            },
            libraries,
            library: draggedLibrary,
          });
        }
      },
      [onLibraryRearrange, libraries],
    );

    const renderItem = useCallback(
      ({ index, item }: { index: number; item: LibraryWithPermissions }) => {
        return (
          <div className="relative" key={item.id}>
            {index === 0 && (
              <DNDRearrangeArea
                canDropItem={canRearrangeLibrary(-1)}
                dragTypes={DRAG_TYPES}
                dropLocation="sub-nav-rearrange"
                onItemRearrange={handleItemRearrange(-1)}
                side="top"
              />
            )}
            <WorkspaceSidebarLibrariesListItem key={item.id} library={item} draggable={draggable} index={index} />
            <DNDRearrangeArea
              canDropItem={canRearrangeLibrary(index)}
              dragTypes={DRAG_TYPES}
              dropLocation="sub-nav-rearrange"
              onItemRearrange={handleItemRearrange(index)}
              side="bottom"
            />
          </div>
        );
      },
      [canRearrangeLibrary, handleItemRearrange, draggable],
    );

    return (
      <>
        <WorkspaceSidebarLibrariesListItemGeneral />

        {scrollElement && isScrollElementVisible ? (
          <WorkspaceSidebarVirtualizedList<LibraryWithPermissions>
            items={libraries}
            renderItem={renderItem}
            scrollElement={scrollElement}
          />
        ) : (
          libraries.map((item, index) => renderItem({ index, item }))
        )}
      </>
    );
  },
);

WorkspaceSidebarLibrariesList.displayName = 'WorkspaceSidebarLibrariesList';
